/* eslint-disable @typescript-eslint/return-await */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Tab, Typography } from '@mui/material'
import { TabContext, TabList } from '@mui/lab'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DivisionManager } from '../../../../APIManager'
import ROUTES from '../../../../consts/paths'
import { type Row } from '../../../../components/CustomTable/types'
import LeadText from '../../../../components/CustomTable/CellTypes/LeadText'
import { renderInstructorDetails, renderSurveyScore } from './TableColumnsCell'
import { type TableCell, type ExtendedParams } from '../../../../models/CompletionRate'
import { grey } from '../../../../colors'
import { type SurveyCompletionRate } from '../../../../models/Division'
import AppTable from '../../../../components/CustomTable'

interface SectionTableFeatureProps {
  parentSurveyScore?: number | null
  completionRate?: SurveyCompletionRate | null
}

export const SectionTableFeature: React.FC<SectionTableFeatureProps> = ({
  parentSurveyScore,
  completionRate
}) => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const termId = searchParams.get('termId') ?? undefined
  const distributionSeriesId = searchParams.get('projectId') ?? undefined
  const categoryName = completionRate?.categoryName

  const [preparedTabs, setPreparedTabs] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState<string>(categoryName ?? '')
  const [params, setParams] = useState<ExtendedParams>({
    termId,
    distributionSeriesId,
    pageSize: 5,
    search: ''
  })

  const { data: childDivisionsTabs = [], isLoading: isLoadingChildDivisionsTabs } = useQuery({
    queryKey: ['childDivisionsTabs', categoryName, id],
    queryFn: async () => DivisionManager.getCategoryTabsChildren(categoryName ?? ''),
    select: (data) => data ?? [],
    enabled: !!selectedTab
  })

  const { data: CDG, isLoading } = useQuery({
    queryKey: ['completionRatesChildren', id, termId, distributionSeriesId, params, selectedTab],
    queryFn: async () => DivisionManager.getSectionTable(id ?? '', selectedTab, params),
    enabled: !!id && !!termId && !!distributionSeriesId
  })

  const tabsWithSection = useMemo(() => {
    return childDivisionsTabs.map((c: { categoryName: string }) => c.categoryName)
  }, [childDivisionsTabs])

  useEffect(() => {
    if (!isLoadingChildDivisionsTabs) {
      const prepared = [...tabsWithSection, 'Section']
      setPreparedTabs(prepared)
      setSelectedTab(prepared[0] ?? '')
    }
  }, [isLoadingChildDivisionsTabs, tabsWithSection, childDivisionsTabs.length])

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue)
    setParams((prev) => ({ ...prev, search: '' }))
  }, [])

  const handleCellClick = useCallback((row: Row) => {
    const division = row.row.data.divisionId ?? row.row.data.sectionId
    navigate(`${ROUTES.REPORTS}/${division}?${searchParams.toString()}`)
  }, [navigate, searchParams])

  const overrides = useMemo(() => ({
    categoryName: (cell: TableCell) => (
      <LeadText onClick={() => { handleCellClick(cell) }}>
        {cell.categoryName}
      </LeadText>
    ),
    completionPercent: (cell: TableCell) => (
      !cell?.isAnonymousSurvey
        ? <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {(cell.completionPercent as number * 100).toFixed(0)}%
          </Typography>
        : <Typography sx={{ fontSize: '14px', color: grey[850], fontWeight: 400 }}>
            {cell?.completionCount}
          </Typography>
    ),
    surveyScore: (cell: TableCell) => renderSurveyScore(cell, parentSurveyScore),
    instructorDetails: renderInstructorDetails
  }), [handleCellClick, parentSurveyScore])

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      <TabContext value={selectedTab}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList
            onChange={handleTabChange}
            aria-label="category-tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                fontSize: '16px',
                fontWeight: 600
              }
            }}
          >
            {preparedTabs.map((tab) => (
              <Tab key={tab} label={tab} value={tab} />
            ))}
          </TabList>
        </Box>
      </TabContext>
      <AppTable
        loading={isLoading}
        featureName="Completion Rates"
        params={params}
        setParams={setParams}
        list={CDG?.data}
        onRowClick={handleCellClick}
        overrides={overrides}
      />
    </Box>
  )
}
